import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { BookOpen, Shield, ShieldCheck, ShieldAlert, X } from 'lucide-react';
import { api } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';

type PolizaRow = {
  id: string;
  tipo: 'INGRESO' | 'EGRESO' | 'DIARIO';
  numero: number;
  fecha: string;
  concepto: string;
  total_cargo: number;
  total_abono: number;
  estado: string;
  referencia_origen?: string;
  referencia_id?: string;
};

type LineaDetalle = {
  secuencia: number;
  cuenta_id: string;
  cuenta_codigo: string;
  cuenta_nombre: string;
  cargo: number;
  abono: number;
  concepto: string;
};

type PolizaDetalle = PolizaRow & {
  ejercicio_id: string;
  tipo_poliza_clave: string;
  firma_ed25519?: string;
  lineas: LineaDetalle[];
};

export function PolizasPage() {
  const [selected, setSelected] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['polizas'],
    queryFn: () => api<PolizaRow[]>('/polizas?limit=50'),
  });

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Contabilidad · Pólizas</p>
        <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Libro diario</h1>
        <p className="mt-1 text-[14px] text-ink-mute">
          Cada póliza se firma con Ed25519 al registrarse. La cadena de auditoría evita cambios silenciosos.
        </p>
      </div>

      <Card>
        <CardHeader title="Pólizas recientes" subtitle={data ? `${data.length} pólizas` : ''} />
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-10 text-center text-[13px] text-ink-mute">Cargando…</div>
          ) : !data || data.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <BookOpen size={28} className="text-ink-soft" />
              <p className="text-[13px] text-ink-mute">Sin pólizas registradas todavía.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-[13px]">
                <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                  <tr>
                    <th className="px-5 py-3">Póliza</th>
                    <th className="px-5 py-3">Fecha</th>
                    <th className="px-5 py-3">Concepto</th>
                    <th className="px-5 py-3 text-right">Importe</th>
                    <th className="px-5 py-3">Estado</th>
                    <th className="px-5 py-3">Origen</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.map((p) => (
                    <tr key={p.id} className="transition-colors hover:bg-muted/50">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <TipoBadge t={p.tipo} />
                          <span className="num font-semibold text-ink">#{p.numero}</span>
                        </div>
                      </td>
                      <td className="num px-5 py-3 text-ink-mute">{fmt.date(p.fecha)}</td>
                      <td className="px-5 py-3 max-w-[340px] truncate text-ink">{p.concepto}</td>
                      <td className="num px-5 py-3 text-right font-semibold">{fmt.money(p.total_cargo)}</td>
                      <td className="px-5 py-3"><EstadoPill s={p.estado} /></td>
                      <td className="px-5 py-3 text-[12px] text-ink-mute">{p.referencia_origen ?? '—'}</td>
                      <td className="px-5 py-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => setSelected(p.id)}>Ver</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {selected && <PolizaDrawer id={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function TipoBadge({ t }: { t: PolizaRow['tipo'] }) {
  const styles = {
    INGRESO: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
    EGRESO:  'bg-brand-50    text-brand-700   dark:bg-brand-900/40  dark:text-brand-200',
    DIARIO:  'bg-stone-100   text-ink-mute    dark:bg-white/5',
  } as const;
  return <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide', styles[t])}>{t}</span>;
}

function EstadoPill({ s }: { s: string }) {
  const colors = {
    registrada: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
    borrador:   'bg-amber-50   text-amber-800   dark:bg-amber-900/40   dark:text-amber-200',
    cancelada:  'bg-rose-50    text-rose-700    dark:bg-rose-900/40    dark:text-rose-200',
  } as Record<string,string>;
  return <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', colors[s] ?? 'bg-muted text-ink-mute')}>{s}</span>;
}

function PolizaDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: p, isLoading } = useQuery({
    queryKey: ['poliza', id],
    queryFn: () => api<PolizaDetalle>(`/polizas/${id}`),
  });
  const { data: verify } = useQuery({
    queryKey: ['poliza-verify', id],
    queryFn: () => api<{ firma_valida: boolean }>(`/polizas/${id}/verify`),
    enabled: !!p?.firma_ed25519,
  });

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30 backdrop-blur-sm">
      <div className="flex h-full w-full max-w-[680px] flex-col overflow-y-auto bg-surface shadow-lift">
        <header className="sticky top-0 flex items-start justify-between border-b border-border bg-surface px-6 py-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Póliza</p>
            {p && <h2 className="font-display text-[22px] font-bold text-ink">{p.tipo_poliza_clave} #{p.numero}</h2>}
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-ink-mute hover:bg-muted">
            <X size={18} />
          </button>
        </header>

        {isLoading || !p ? (
          <div className="p-10 text-center text-[13px] text-ink-mute">Cargando…</div>
        ) : (
          <div className="flex flex-1 flex-col gap-6 px-6 py-5">
            <div className="grid grid-cols-2 gap-4 text-[13px]">
              <Info label="Fecha" value={fmt.date(p.fecha)} />
              <Info label="Estado" value={<EstadoPill s={p.estado} />} />
              <Info label="Cargos"  value={<span className="num font-semibold">{fmt.money(p.total_cargo)}</span>} />
              <Info label="Abonos"  value={<span className="num font-semibold">{fmt.money(p.total_abono)}</span>} />
              <Info label="Origen"  value={p.referencia_origen ?? '—'} />
              <Info label="Ref. ID" value={<span className="num text-[11px]">{p.referencia_id ?? '—'}</span>} />
            </div>

            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">Concepto</p>
              <p className="mt-1 text-[13px] text-ink">{p.concepto}</p>
            </div>

            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">Líneas</p>
              <div className="overflow-hidden rounded-md border border-border">
                <table className="w-full text-[12px]">
                  <thead className="bg-muted/60 text-left text-[10px] font-semibold uppercase tracking-wide text-ink-mute">
                    <tr>
                      <th className="px-3 py-2 w-10">#</th>
                      <th className="px-3 py-2">Cuenta</th>
                      <th className="px-3 py-2 text-right">Cargo</th>
                      <th className="px-3 py-2 text-right">Abono</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {p.lineas.map((l) => (
                      <tr key={l.secuencia}>
                        <td className="num px-3 py-2 text-ink-mute">{l.secuencia}</td>
                        <td className="px-3 py-2">
                          <div className="num text-[11px] font-semibold text-brand-700 dark:text-brand-300">{l.cuenta_codigo}</div>
                          <div className="text-[11px] text-ink-mute">{l.cuenta_nombre}</div>
                        </td>
                        <td className="num px-3 py-2 text-right">{l.cargo > 0 ? fmt.money(l.cargo) : '—'}</td>
                        <td className="num px-3 py-2 text-right">{l.abono > 0 ? fmt.money(l.abono) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-md border border-border bg-muted/30 p-4">
              <div className="flex items-center gap-2">
                {p.firma_ed25519 ? (
                  verify?.firma_valida ? (
                    <ShieldCheck className="h-5 w-5 text-emerald-600" />
                  ) : verify ? (
                    <ShieldAlert className="h-5 w-5 text-danger" />
                  ) : (
                    <Shield className="h-5 w-5 text-ink-mute" />
                  )
                ) : (
                  <Shield className="h-5 w-5 text-ink-mute" />
                )}
                <p className="text-[13px] font-semibold text-ink">
                  {p.firma_ed25519
                    ? verify
                      ? verify.firma_valida ? 'Firma Ed25519 válida' : 'Firma inválida — tampering detectado'
                      : 'Verificando firma…'
                    : 'Sin firma (borrador)'}
                </p>
              </div>
              {p.firma_ed25519 && (
                <p className="num mt-2 break-all text-[10px] text-ink-soft">{p.firma_ed25519}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-soft">{label}</p>
      <div className="mt-1 text-ink">{value}</div>
    </div>
  );
}
